import { useContext, useEffect, useState, useCallback } from 'react';
import { Context } from './../Provider';

const defaultSettings = {
  enableHighAccuracy: false,
  timeout: Infinity,
  maximumAge: 0,
};

const FIDES_LOCATION_URL = 'https://cdn-api.ethyca.com/location';

type FidesGeolocation = {
  country: string; // ex. US
  location: string; // ex. US-NY
  region: string; // ex. NY
};

export const useFidesGeolocation = () => {
  const [error, setError] = useState<Error | null>(null);
  const [geolocation, setGeolocation] = useState<FidesGeolocation | null>(null);
  useEffect(() => {
    fetch(FIDES_LOCATION_URL)
      .then(response => response.json())
      .then(data => {
        if (!geolocation) {
          setGeolocation(data);
        }
      })
      .catch(err => {
        setError(err);
      });
  }, [geolocation]);
  return { geolocation, error };
};

export function useGeolocation() {
  const { position, setPosition, geolocation, setGeolocation } = useContext(Context);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Next SSR friendly approach
    setGeolocation(navigator.geolocation);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getGeolocation = useCallback(() => {
    const successCb = (positionCb: GeolocationPosition) => {
      setPosition(positionCb);
      setIsLoading(false);
    };

    const errorCb = (errCb: GeolocationPositionError) => {
      setError(errCb);
      setIsLoading(false);
    };

    setIsLoading(true);
    if (geolocation) {
      geolocation.getCurrentPosition(successCb, errorCb, defaultSettings);
    }
  }, [geolocation, setPosition]);

  return { position, error, isLoading, getGeolocation };
}
